/**
 * The contract promise: a client doesn't get the same pot again within 12 months.
 *
 * This is the ONLY place the boundary is defined. Two placements conflict when they are less
 * than `windowMonths` apart, measured from the placement date in both directions:
 *     conflict(p, q)  ⇔  q < p + window  AND  p < q + window
 * Example: placed 15 Mar 2026 → blocked until 14 Mar 2027, allowed again ON 15 Mar 2027.
 *
 * A missing record is not proof of "never placed". If the window reaches back before the
 * history we actually have, the verdict is `unverified` (amber), not `allowed`.
 */
import { addDays, addMonths, type CalendarDate } from "./calendar";
import type { History, Placement } from "./history";

export interface RepeatPolicy {
  readonly windowMonths: number;
  /** Rule applies per client (any spot) or per spot. Open question: assume "client". */
  readonly scope: "client" | "slot";
}

export const DEFAULT_REPEAT_POLICY: RepeatPolicy = { windowMonths: 12, scope: "client" };

export interface PlacementQuery {
  readonly potId: string;
  readonly clientId: string;
  readonly slotId: string;
  readonly on: CalendarDate;
}

export type RepeatVerdict =
  | { readonly status: "allowed" }
  | {
      readonly status: "blocked";
      readonly lastPlacedOn: CalendarDate;
      /** First day the pot may go to this client again. */
      readonly allowedFrom: CalendarDate;
    }
  | {
      readonly status: "unverified";
      /** We only have complete records from this day, so earlier placements can't be ruled out. */
      readonly historyKnownSince: CalendarDate;
    };

function sameScope(p: Placement, q: PlacementQuery, scope: RepeatPolicy["scope"]): boolean {
  return scope === "client" ? p.clientId === q.clientId : p.slotId === q.slotId;
}

/**
 * @param historyKnownSince  first day from which the tenant's records are complete
 *                           (e.g. the earliest month imported from her spreadsheet)
 */
export function checkRepeatRule(
  query: PlacementQuery,
  history: History,
  policy: RepeatPolicy,
  historyKnownSince: CalendarDate,
): RepeatVerdict {
  const { windowMonths } = policy;
  let latestConflict: CalendarDate | undefined;

  for (const p of history.placementsOf(query.potId)) {
    if (!sameScope(p, query, policy.scope)) continue;
    const conflicts = query.on < addMonths(p.placedOn, windowMonths) && p.placedOn < addMonths(query.on, windowMonths);
    if (conflicts && (latestConflict === undefined || p.placedOn > latestConflict)) latestConflict = p.placedOn;
  }

  if (latestConflict !== undefined) {
    return { status: "blocked", lastPlacedOn: latestConflict, allowedFrom: addMonths(latestConflict, windowMonths) };
  }

  // The latest unknown day that could hide a conflicting placement is the day before our records start.
  const lastUnknownDay = addDays(historyKnownSince, -1);
  if (query.on < addMonths(lastUnknownDay, windowMonths)) {
    return { status: "unverified", historyKnownSince };
  }

  return { status: "allowed" };
}
